import { Link } from "react-router-dom";
import { ArrowRight, Phone, ShieldCheck, Scale, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import { ATTORNEY, PRACTICE_AREAS, DAMAGES, FAQS } from "@/data/content";
import { POSTS } from "@/data/blog";

export default function Home() {
    return (
        <>
            <SEO
                title="Sacramento Personal Injury Attorney"
                description="Joseph G. Helfrick — Sacramento-born personal injury attorney representing the seriously injured throughout Sacramento, CA, and surrounding areas. Free, confidential consultation. No fee unless we win."
                path="/"
                faqs={FAQS}
                breadcrumbs={[{ name: "Home", path: "/" }]}
            />

            {/* HERO */}
            <section
                data-testid="hero"
                className="relative overflow-hidden border-b border-[#1F3458]"
            >
                {/* Tower Bridge — Sacramento, with Ken Burns motion */}
                <div
                    aria-hidden
                    className="absolute inset-0 bg-cover bg-center ken-burns"
                    style={{
                        backgroundImage:
                            "url('https://images.pexels.com/photos/35120854/pexels-photo-35120854.jpeg?auto=compress&cs=tinysrgb&w=2400')",
                    }}
                />
                {/* Asymmetric dual gradient — readable text left, photo breathes right */}
                <div
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-r from-[#07142B] via-[#07142B]/85 to-transparent"
                />
                <div
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-t from-[#07142B] via-transparent to-transparent"
                />
                {/* Gold spotlight glow — top-right corner accent */}
                <div
                    aria-hidden
                    className="gold-glow pointer-events-none absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full"
                    style={{
                        background:
                            "radial-gradient(circle, rgba(212,175,55,0.28) 0%, rgba(212,175,55,0.08) 35%, transparent 65%)",
                        filter: "blur(20px)",
                    }}
                />
                {/* Soft vignette to anchor the headline */}
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(120% 80% at 50% 100%, transparent 40%, rgba(7,20,43,0.5) 100%)",
                    }}
                />
                {/* Hairline gold ruler at top of hero — editorial feel */}
                <div
                    aria-hidden
                    className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent"
                />
                {/* Tiny "city light" flickers, decorative only */}
                <div
                    aria-hidden
                    className="pointer-events-none hidden md:block"
                >
                    <span className="absolute top-[28%] right-[18%] w-1 h-1 rounded-full bg-[#D4AF37] flicker-1" />
                    <span className="absolute top-[42%] right-[34%] w-[3px] h-[3px] rounded-full bg-[#E6C869] flicker-2" />
                    <span className="absolute top-[60%] right-[12%] w-1 h-1 rounded-full bg-[#D4AF37] flicker-3" />
                </div>
                <div
                    aria-hidden
                    className="absolute inset-0 grain pointer-events-none"
                />

                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-24 pb-32 lg:pt-36 lg:pb-44 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-8">
                        <div
                            data-testid="hero-overline"
                            className="rise flex items-center gap-3 text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold"
                        >
                            <span className="h-px w-10 bg-[#D4AF37]" />
                            Sacramento, CA · Surrounding Areas · Northern California
                        </div>
                        <h1
                            data-testid="hero-title"
                            className="rise-2 mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-[#F7F5EB] leading-[0.95] tracking-tight font-light"
                        >
                            When the worst day{" "}
                            <span className="italic text-[#D4AF37]">
                                of your life
                            </span>{" "}
                            wasn't your fault.
                        </h1>
                        <p
                            data-testid="hero-sub"
                            className="rise-3 mt-8 text-lg md:text-xl text-[#9FB3D4] max-w-2xl leading-relaxed"
                        >
                            Joe Helfrick is a Sacramento-born trial attorney who
                            represents people seriously hurt by careless
                            drivers, doctors, and property owners —
                            as well as those accused of the same.
                        </p>

                        <div
                            data-testid="hero-ctas"
                            className="rise-4 mt-10 flex flex-wrap items-center gap-4"
                        >
                            <Link
                                to="/contact"
                                data-testid="hero-cta-primary"
                                className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#07142B] font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-[#B8962A] transition-colors"
                            >
                                Free Case Review
                                <ArrowRight size={16} />
                            </Link>
                            <a
                                data-testid="hero-cta-phone"
                                href={`tel:${ATTORNEY.phone.replace(/[^0-9]/g, "")}`}
                                className="inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-[#D4AF37] hover:text-[#07142B] transition-colors"
                            >
                                <Phone size={14} />
                                {ATTORNEY.phone}
                            </a>
                        </div>

                        <div className="rise-4 mt-6">
                            <Link
                                to="/case-evaluator"
                                data-testid="hero-cta-evaluator"
                                className="text-[#9FB3D4] text-sm underline underline-offset-4 decoration-[#1F3458] hover:text-[#D4AF37] hover:decoration-[#D4AF37] transition-colors"
                            >
                                Not sure you have a case? Take the 60-second
                                case evaluator →
                            </Link>
                        </div>

                        <div
                            data-testid="hero-trust"
                            className="rise-4 mt-14 grid grid-cols-2 sm:grid-cols-3 gap-6 max-w-2xl"
                        >
                            {[
                                {
                                    k: "Free",
                                    v: "Confidential consultation",
                                },
                                {
                                    k: "Local",
                                    v: "Sacramento born & raised",
                                },
                            ].map((t) => (
                                <div
                                    key={t.k}
                                    className="border-l border-[#D4AF37]/60 pl-4"
                                >
                                    <div className="font-serif text-2xl text-[#F7F5EB]">
                                        {t.k}
                                    </div>
                                    <div className="text-xs text-[#9FB3D4] mt-1">
                                        {t.v}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Portrait card */}
                    <div className="lg:col-span-4 relative">
                        <div className="rise-3 relative aspect-[3/4] max-w-sm ml-auto border border-[#1F3458] bg-[#11203D]">
                            <img
                                src="/images/joe-helfrick-portrait.jpg"
                                alt="Joseph G. Helfrick — Sacramento Personal Injury Attorney"
                                className="absolute inset-0 w-full h-full object-cover object-center"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#07142B] via-transparent to-transparent" />
                            <div className="absolute -bottom-6 -left-6 bg-[#07142B] border border-[#D4AF37] px-6 py-4">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
                                    Attorney at Law
                                </div>
                                <div className="font-serif text-2xl text-[#F7F5EB]">
                                    Joseph G. Helfrick
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* INTRO STATEMENT */}
            <section
                data-testid="intro"
                className="relative border-b border-[#1F3458]"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold">
                            01 — Who Joe Is
                        </div>
                        <h2 className="mt-6 font-serif text-4xl lg:text-5xl text-[#F7F5EB] leading-tight">
                            A Sacramento lawyer who learned trial work the hard
                            way.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 lg:pl-12 lg:border-l lg:border-[#1F3458]">
                        <p className="text-lg text-[#F7F5EB]/90 leading-relaxed">
                            {ATTORNEY.bioShort}
                        </p>
                        <p className="mt-6 text-base text-[#9FB3D4] leading-relaxed">
                            Joe started his career representing major corporate
                            and banking clients in courtrooms across Northern
                            California, then spent years on the defense side of
                            personal injury and medical malpractice cases —
                            learning exactly how the other side builds its file.
                            He now uses that knowledge for the people on the
                            receiving end of someone else's carelessness.
                        </p>
                        <Link
                            to="/about"
                            data-testid="intro-about-link"
                            className="mt-8 inline-flex items-center gap-2 text-[#D4AF37] font-semibold border-b border-[#D4AF37]/40 hover:border-[#D4AF37] pb-1 transition-colors"
                        >
                            More about Joe <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* PRACTICE AREAS BENTO */}
            <section
                data-testid="home-practice"
                className="border-b border-[#1F3458]"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
                    <div className="grid lg:grid-cols-12 gap-12 mb-16">
                        <div className="lg:col-span-5">
                            <div className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold">
                                02 — Practice Areas
                            </div>
                            <h2 className="mt-6 font-serif text-4xl lg:text-5xl text-[#F7F5EB] leading-tight">
                                The cases we take. The cases we win.
                            </h2>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7">
                            <p className="text-[#9FB3D4] text-lg leading-relaxed">
                                A focused personal-injury practice covering the
                                six categories of cases we see most often
                                throughout Sacramento, CA, and surrounding areas — from
                                the freeway collision to the surgical error to
                                the dog bite next door.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
                        {PRACTICE_AREAS.map((p, i) => {
                            const span =
                                p.size === "large"
                                    ? "md:col-span-8"
                                    : "md:col-span-4";
                            return (
                                <Link
                                    key={p.slug}
                                    to={`/practice-areas#${p.slug}`}
                                    data-testid={`home-practice-${p.slug}`}
                                    className={`group ${span} relative bg-[#11203D] border border-[#1F3458] hover:border-[#D4AF37] hover:bg-[#1A2D50] transition-all p-8 md:p-10 flex flex-col`}
                                >
                                    <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.24em] font-bold">
                                        0{i + 1}
                                    </div>
                                    <h3 className="mt-4 font-serif text-3xl md:text-4xl text-[#F7F5EB] leading-tight">
                                        {p.title}
                                    </h3>
                                    <p className="mt-4 text-[#9FB3D4] text-sm md:text-base leading-relaxed">
                                        {p.lede}
                                    </p>
                                    <div className="mt-auto pt-8 flex items-center justify-between text-[#D4AF37]">
                                        <span className="text-xs uppercase tracking-[0.22em] font-bold">
                                            Learn more
                                        </span>
                                        <ArrowRight
                                            size={18}
                                            className="group-hover:translate-x-1 transition-transform"
                                        />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* DAMAGES STAGGERED */}
            <section
                data-testid="home-damages"
                className="border-b border-[#1F3458] bg-[#0B1A36]"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
                    <div className="grid lg:grid-cols-12 gap-12 mb-16">
                        <div className="lg:col-span-5">
                            <div className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold">
                                03 — What You Can Recover
                            </div>
                            <h2 className="mt-6 font-serif text-4xl lg:text-5xl text-[#F7F5EB] leading-tight">
                                Damages.
                                <br />
                                One full picture of the harm.
                            </h2>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7">
                            <p className="text-[#9FB3D4] text-lg leading-relaxed">
                                California law recognizes several kinds of
                                compensation in a personal-injury case. Most
                                insurance adjusters will only talk about the
                                first. We make sure every category that applies
                                is on the table.
                            </p>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {DAMAGES.map((d, i) => (
                            <Link
                                key={d.slug}
                                to={`/damages#${d.slug}`}
                                data-testid={`home-damage-${d.slug}`}
                                className={`group bg-[#11203D] border border-[#1F3458] hover:border-[#D4AF37] p-8 md:p-10 transition-colors ${
                                    i === 1
                                        ? "md:mt-12"
                                        : i === 2
                                          ? "md:mt-24"
                                          : ""
                                }`}
                            >
                                <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.24em] font-bold">
                                    {d.type}
                                </div>
                                <h3 className="mt-3 font-serif text-3xl text-[#F7F5EB] leading-tight">
                                    {d.tagline}
                                </h3>
                                <p className="mt-4 text-[#9FB3D4] text-sm leading-relaxed">
                                    {d.oneliner}
                                </p>
                                <div className="mt-6 inline-flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.22em] font-bold">
                                    Read more{" "}
                                    <ArrowRight
                                        size={14}
                                        className="group-hover:translate-x-1 transition-transform"
                                    />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* CRED STRIP */}
            <section
                data-testid="home-creds"
                className="border-b border-[#1F3458]"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid md:grid-cols-3 gap-8">
                    {[
                        {
                            Icon: ShieldCheck,
                            title: "California Bar — 2017",
                            sub: "U.S. District Court, Eastern District of California — 2020",
                        },
                        {
                            Icon: Scale,
                            title: "Lincoln Law School, J.D. — 2015",
                            sub: "Faculty Achievement Award · Legal Analysis",
                        },
                        {
                            Icon: MapPin,
                            title: "Sacramento, Born & Raised",
                            sub: "Jesuit High School · Holy Cross College of Notre Dame",
                        },
                    ].map((c) => (
                        <div
                            key={c.title}
                            className="flex items-start gap-5 border-l border-[#D4AF37]/40 pl-6"
                        >
                            <c.Icon
                                size={28}
                                className="text-[#D4AF37] shrink-0 mt-1"
                            />
                            <div>
                                <div className="font-serif text-xl text-[#F7F5EB] leading-tight">
                                    {c.title}
                                </div>
                                <div className="text-sm text-[#9FB3D4] mt-2">
                                    {c.sub}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* FEATURED ARTICLE */}
            <section
                data-testid="home-featured-article"
                className="border-b border-[#1F3458]"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-28 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold">
                            From the Blog
                        </div>
                        <h2 className="mt-6 font-serif text-4xl lg:text-5xl text-[#F7F5EB] leading-tight">
                            Plain-English guides from a Sacramento trial
                            attorney.
                        </h2>
                        <p className="mt-6 text-[#9FB3D4] text-base leading-relaxed max-w-md">
                            No filler — just the questions Joe answers every
                            week from people just hurt in Sacramento, CA, and
                            surrounding areas.
                        </p>
                        <Link
                            to="/blog"
                            data-testid="home-blog-all"
                            className="mt-6 inline-flex items-center gap-2 text-[#D4AF37] font-semibold border-b border-[#D4AF37]/40 hover:border-[#D4AF37] pb-1 transition-colors"
                        >
                            View all articles <ArrowRight size={14} />
                        </Link>
                    </div>
                    <div className="lg:col-span-7">
                        {POSTS.slice(0, 1).map((p) => (
                            <Link
                                key={p.slug}
                                to={`/blog/${p.slug}`}
                                data-testid={`home-blog-${p.slug}`}
                                className="group block bg-[#11203D] border border-[#1F3458] hover:border-[#D4AF37] transition-colors p-8 md:p-10"
                            >
                                <div className="text-[#9FB3D4] text-[11px] uppercase tracking-[0.22em]">
                                    {p.category} · {p.readTime}
                                </div>
                                <h3 className="mt-4 font-serif text-2xl md:text-3xl text-[#F7F5EB] leading-tight">
                                    {p.title}
                                </h3>
                                <p className="mt-4 text-[#9FB3D4] text-sm md:text-base leading-relaxed">
                                    {p.excerpt}
                                </p>
                                <div className="mt-6 inline-flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.22em] font-bold">
                                    Read article{" "}
                                    <ArrowRight
                                        size={14}
                                        className="group-hover:translate-x-1 transition-transform"
                                    />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA BAND */}
            <section
                data-testid="home-cta"
                className="relative overflow-hidden"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-7">
                        <div className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold">
                            04 — Talk to Joe
                        </div>
                        <h2 className="mt-6 font-serif text-5xl lg:text-6xl text-[#F7F5EB] leading-[1.02]">
                            Tell us what happened.
                            <br />
                            <span className="italic text-[#D4AF37]">
                                The first call is free.
                            </span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5">
                        <p className="text-[#9FB3D4] text-base leading-relaxed">
                            Insurance companies move fast. So should you. The
                            sooner Joe is involved, the more evidence we can
                            preserve and the harder it is for the other side to
                            shape the story.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <Link
                                to="/contact"
                                data-testid="home-cta-button"
                                className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#07142B] font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-[#B8962A] transition-colors"
                            >
                                Start Your Free Case Review{" "}
                                <ArrowRight size={16} />
                            </Link>
                            <Link
                                to="/case-evaluator"
                                data-testid="home-cta-evaluator"
                                className="inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-[#D4AF37] hover:text-[#07142B] transition-colors"
                            >
                                60-Second Case Evaluator
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
